import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Download, Upload, LayoutGrid, List, PhoneCall, CalendarClock, MessageCircle, Ban, RefreshCw, Loader2, CheckCircle2, Circle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { PageHeader, EmptyState, TableSkeleton } from "@/components/common";
import { DirectionBadge, ScoreBadge, CategoryBadge, StatusPill, LANG_NAMES, fmtTime } from "@/lib/format";
import api, { apiErr } from "@/lib/api";

const KANBAN = ["New Inbound", "Pending", "Calling", "Qualified", "Demo Booked", "Converted"];

function LeadDrawer({ id, onClose, onChange }) {
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get(`/leads/${id}`).then((r) => { setLead(r.data); setNotes((prev) => (prev === "" || prev === undefined ? r.data.notes || "" : prev)); });
  }, [id]);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  const act = async (fn, msg) => { setBusy(msg); try { await fn(); toast.success(msg); load(); onChange && onChange(); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); } };

  if (!lead) return <div className="p-8 flex justify-center"><SheetHeader className="sr-only"><SheetTitle>Lead details</SheetTitle></SheetHeader><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;

  return (
    <div className="flex flex-col h-full">
      <SheetHeader className="px-6 pt-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-bold">{lead.name?.slice(0,1)}</div>
          <div>
            <SheetTitle className="font-display">{lead.name}</SheetTitle>
            <div className="flex items-center gap-2 mt-1"><DirectionBadge direction={lead.direction} /><CategoryBadge category={lead.category} /></div>
          </div>
        </div>
      </SheetHeader>

      <Tabs defaultValue="overview" className="flex-1 flex flex-col overflow-hidden mt-4">
        <TabsList className="mx-6"><TabsTrigger value="overview" data-testid="drawer-tab-overview">Overview</TabsTrigger><TabsTrigger value="calls">Call History</TabsTrigger><TabsTrigger value="transcript">Transcript</TabsTrigger><TabsTrigger value="actions">Actions</TabsTrigger></TabsList>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <TabsContent value="overview" className="mt-0 space-y-4">
            <Card className="border shadow-none p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Lead Profile</div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[["Lead ID", lead.lead_id],["Client ID", lead.client_id||"—"],["Phone", lead.mobile],["City", lead.city||"—"],["Source", lead.source],["Enquiry", lead.enquiry_date],["AB Variant", lead.ab_variant],["WhatsApp Req.", lead.whatsapp_requested?"Yes":"No"]].map(([l,v]) => (
                  <div key={l}><div className="text-[11px] text-slate-400">{l}</div><div className="font-medium">{v}</div></div>
                ))}
              </div>
            </Card>

            <Card className="border shadow-none p-4">
              <div className="flex items-center justify-between mb-2"><div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Discussion Summary</div>
                <Button size="sm" variant="ghost" onClick={() => act(() => api.post(`/leads/${id}/regenerate-summary`), "Summary regenerated")} disabled={busy}><RefreshCw className={`w-3 h-3 mr-1 ${busy==="Summary regenerated"?"animate-spin":""}`} />Regenerate</Button>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-300">{lead.discussion_summary || "No summary yet."}</p>
              <div className="text-[11px] text-slate-400 mt-2 flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Auto-updated after each call{lead.summary_updated_at ? ` · Last updated ${fmtTime(lead.summary_updated_at)}` : ""}</div>
            </Card>

            <Card className="border shadow-none p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Qualification Status</div>
              <div className="space-y-2">
                {lead.criteria_def.map((c) => {
                  const val = lead.criterion_answers?.[c.key];
                  return <div key={c.key} className="flex items-center gap-2 text-sm">{val ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Circle className="w-4 h-4 text-slate-300" />}<span className="font-medium">{c.label}:</span><span className="text-slate-500">{val || "Not collected"}</span></div>;
                })}
              </div>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t"><ScoreBadge score={lead.score} /><span className="text-sm text-slate-500">Score {lead.score}/100 — {lead.category}</span></div>
            </Card>

            <Card className="border shadow-none p-4">
              <Label className="text-xs uppercase tracking-wider text-slate-400">Notes</Label>
              <Textarea className="mt-2" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="lead-notes" />
              <Button size="sm" className="mt-2" onClick={() => act(() => api.patch(`/leads/${id}`, { notes }), "Notes saved")}>Save Notes</Button>
            </Card>
          </TabsContent>

          <TabsContent value="calls" className="mt-0 space-y-3">
            {lead.calls?.length ? lead.calls.map((c) => (
              <Card key={c.id} className="border shadow-none p-3">
                <div className="flex items-center gap-2"><DirectionBadge direction={c.direction} /><span className="text-sm font-medium">{c.outcome}</span><ScoreBadge score={c.score} /><span className="ml-auto text-xs text-slate-400">{fmtTime(c.started_at)}</span></div>
              </Card>
            )) : <EmptyState icon={PhoneCall} title="No calls yet" description="Trigger an outbound call from the Actions tab." />}
          </TabsContent>

          <TabsContent value="transcript" className="mt-0 space-y-4">
            {lead.calls?.some(c => c.transcript) ? lead.calls.filter(c => c.transcript).map((c) => (
              <Card key={c.id} className="border shadow-none p-4">
                <div className="flex items-center gap-2 mb-2"><DirectionBadge direction={c.direction} /><span className="text-xs text-slate-400">{fmtTime(c.started_at)}</span></div>
                <div className="space-y-2">
                  {c.transcript.split("\n").filter(l => l.trim()).map((line, i) => {
                    const idx = line.indexOf(":");
                    const spk = idx > 0 ? line.slice(0, idx).trim() : "";
                    const text = idx > 0 ? line.slice(idx + 1).trim() : line;
                    const isAgent = /akriti|assistant|\bai\b|agent|bot|system/i.test(spk);
                    const label = isAgent ? "AKRITI" : (spk && !/caller|user|human|customer|lead|client/i.test(spk) ? spk.toUpperCase() : "CALLER");
                    return <div key={i} className={`text-sm p-2 rounded-lg ${isAgent ? "bg-indigo-50 dark:bg-indigo-500/10" : "bg-slate-50 dark:bg-slate-800"}`}><span className="font-semibold mr-1">{label}:</span>{text}</div>;
                  })}
                </div>
              </Card>
            )) : <EmptyState icon={MessageCircle} title="No transcript available" description="Transcripts appear after a call completes." />}
          </TabsContent>

          <TabsContent value="actions" className="mt-0 space-y-3">
            <Button className="w-full justify-start" variant="outline" onClick={() => act(() => api.post(`/trigger-call`, { lead_id: id }), "Outbound call triggered")} data-testid="action-call"><PhoneCall className="w-4 h-4 mr-2 text-blue-500" /> Trigger Outbound Call</Button>
            <Button className="w-full justify-start" variant="outline" onClick={() => act(() => api.post(`/leads/${id}/callback`, {}), "Callback scheduled")}><CalendarClock className="w-4 h-4 mr-2 text-indigo-500" /> Schedule Callback</Button>
            <Button className="w-full justify-start" variant="outline" onClick={() => act(() => api.post(`/leads/${id}/whatsapp`, { template: "follow_up" }), "WhatsApp sent")}><MessageCircle className="w-4 h-4 mr-2 text-emerald-500" /> Send WhatsApp</Button>
            <Button className="w-full justify-start" variant="outline" onClick={() => act(() => api.post(`/leads/${id}/dnc`), "Marked DNC")}><Ban className="w-4 h-4 mr-2 text-red-500" /> Mark Do-Not-Call</Button>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

export default function Leads() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [view, setView] = useState("table");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ direction: "all", category: "", status: "" });
  const [selected, setSelected] = useState(params.get("lead"));

  const load = useCallback(() => {
    const q = new URLSearchParams({ limit: "100" });
    if (search) q.set("search", search);
    if (filters.direction !== "all") q.set("direction", filters.direction);
    if (filters.category) q.set("category", filters.category);
    if (filters.status) q.set("status", filters.status);
    api.get(`/leads?${q}`).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0 }));
  }, [search, filters]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    const h = (e) => { if (e.target.closest("input,textarea")) return; if (e.key === "f") { e.preventDefault(); document.getElementById("lead-search")?.focus(); } if (e.key === "e") exportCsv(); };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  });

  const exportCsv = () => {
    if (!data?.items?.length) return;
    const cols = ["lead_id","name","mobile","city","interest","language","score","category","direction","status","call_attempts"];
    const rows = [cols.join(","), ...data.items.map((l) => cols.map((c) => `"${l[c] ?? ""}"`).join(","))];
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "leads.csv"; a.click();
    toast.success("Exported CSV");
  };

  const openLead = (id) => { setSelected(id); setParams({ lead: id }); };
  const closeLead = () => { setSelected(null); setParams({}); };

  return (
    <div>
      <PageHeader title="Leads" subtitle={data ? `${data.total} total leads` : "Loading..."}>
        <div className="flex gap-1 border rounded-lg p-0.5">
          <button className={`px-2 py-1 rounded ${view==="table"?"bg-indigo-100 text-indigo-600 dark:bg-indigo-500/15":""}`} onClick={() => setView("table")} data-testid="view-table"><List className="w-4 h-4" /></button>
          <button className={`px-2 py-1 rounded ${view==="kanban"?"bg-indigo-100 text-indigo-600 dark:bg-indigo-500/15":""}`} onClick={() => setView("kanban")} data-testid="view-kanban"><LayoutGrid className="w-4 h-4" /></button>
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="export-btn"><Download className="w-4 h-4 mr-1" />Export</Button>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]"><Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" /><Input id="lead-search" className="pl-9" placeholder="Search name, phone, lead ID..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="lead-search" /></div>
        <Select value={filters.direction} onValueChange={(v) => setFilters((f) => ({ ...f, direction: v }))}><SelectTrigger className="w-36" data-testid="filter-direction"><SelectValue placeholder="Direction" /></SelectTrigger><SelectContent><SelectItem value="all">All Directions</SelectItem><SelectItem value="outbound">Outbound</SelectItem><SelectItem value="inbound">Inbound</SelectItem><SelectItem value="both">Both</SelectItem></SelectContent></Select>
        <Select value={filters.category || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, category: v === "all" ? "" : v }))}><SelectTrigger className="w-32"><SelectValue placeholder="Category" /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="HOT">HOT</SelectItem><SelectItem value="WARM">WARM</SelectItem><SelectItem value="COLD">COLD</SelectItem></SelectContent></Select>
      </div>

      {!data ? <Card className="border shadow-none"><TableSkeleton cols={7} /></Card> :
        data.items.length === 0 ? <Card className="border shadow-none"><EmptyState icon={Upload} title="No leads found" description="Import leads from Google Sheets or adjust your filters." /></Card> :
        view === "table" ? (
          <TooltipProvider delayDuration={200}>
          <Card className="border shadow-none overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 border-b bg-slate-50 dark:bg-slate-800/50"><tr>
                  {["Lead ID","Name","Phone","City","Interest","Lang","Score","Category","Summary","Direction","Status","Attempts","Last Call"].map((h) => <th key={h} className="text-left px-3 py-3 font-medium whitespace-nowrap">{h}</th>)}
                </tr></thead>
                <tbody>
                  {data.items.map((l) => (
                    <tr key={l.id} className="border-b last:border-0 hover:bg-accent cursor-pointer" onClick={() => openLead(l.id)} data-testid={`lead-row-${l.id}`}>
                      <td className="px-3 py-2.5 font-mono text-xs">{l.lead_id}</td>
                      <td className="px-3 py-2.5 font-medium whitespace-nowrap">{l.name}</td>
                      <td className="px-3 py-2.5 text-slate-500">{l.mobile}</td>
                      <td className="px-3 py-2.5 text-slate-500">{l.city||"—"}</td>
                      <td className="px-3 py-2.5 text-slate-500 max-w-[140px] truncate">{l.interest}</td>
                      <td className="px-3 py-2.5 text-slate-500 text-xs">{LANG_NAMES[l.language]||l.language}</td>
                      <td className="px-3 py-2.5"><ScoreBadge score={l.score} /></td>
                      <td className="px-3 py-2.5"><CategoryBadge category={l.category} /></td>
                      <td className="px-3 py-2.5 max-w-[220px]" data-testid={`lead-summary-${l.id}`}>
                        {l.discussion_summary ? (
                          <Tooltip><TooltipTrigger asChild><span className="text-xs text-slate-500 line-clamp-1 cursor-help block max-w-[210px]">{l.discussion_summary.slice(0, 80)}{l.discussion_summary.length > 80 ? "…" : ""}</span></TooltipTrigger><TooltipContent className="max-w-xs"><p className="text-xs leading-relaxed">{l.discussion_summary}</p></TooltipContent></Tooltip>
                        ) : <span className="text-xs text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5"><DirectionBadge direction={l.direction} /></td>
                      <td className="px-3 py-2.5"><StatusPill status={l.status} /></td>
                      <td className="px-3 py-2.5 text-center">{l.call_attempts}</td>
                      <td className="px-3 py-2.5 text-slate-400 text-xs whitespace-nowrap">{fmtTime(l.last_call_time)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          </TooltipProvider>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {KANBAN.map((stage) => {
              const items = data.items.filter((l) => l.status === stage);
              return (
                <div key={stage} className="min-w-[260px] flex-1">
                  <div className="flex items-center justify-between mb-2 px-1"><span className="text-sm font-semibold font-display">{stage}</span><span className="text-xs text-slate-400">{items.length}</span></div>
                  <div className="space-y-2">
                    {items.map((l) => (
                      <Card key={l.id} className="border shadow-none p-3 cursor-pointer hover:border-indigo-300" onClick={() => openLead(l.id)}>
                        <div className="flex items-center justify-between"><span className="font-medium text-sm">{l.name}</span><ScoreBadge score={l.score} /></div>
                        <div className="text-xs text-slate-400 mt-1">{l.city} · {l.interest}</div>
                        <div className="mt-2"><DirectionBadge direction={l.direction} /></div>
                      </Card>
                    ))}
                    {items.length === 0 && <div className="text-xs text-slate-300 text-center py-6 border border-dashed rounded-lg">Empty</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )
      }

      <Sheet open={!!selected} onOpenChange={(o) => !o && closeLead()}>
        <SheetContent className="w-full sm:max-w-lg p-0 overflow-hidden">
          {selected && <LeadDrawer id={selected} onClose={closeLead} onChange={load} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
